import React, { useRef, useState, useEffect, useMemo } from "react";
import { API } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, PenTool, RotateCcw, Trash2, Plus, ShieldCheck, Type } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";

const money = (n) => (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

/* ---------- Signature Pad with "Adopt Signature" (style picker + draw) ---------- */
const SIG_FONTS = [
  { key: "great-vibes", label: "Elegant", family: "'Great Vibes', cursive" },
  { key: "dancing-script", label: "Modern", family: "'Dancing Script', cursive" },
  { key: "sacramento", label: "Classic", family: "'Sacramento', cursive" },
  { key: "homemade-apple", label: "Handwritten", family: "'Homemade Apple', cursive" },
  { key: "caveat", label: "Casual", family: "'Caveat', cursive" },
];

function renderNameToPng(name, fontFamily) {
  const c = document.createElement("canvas");
  c.width = 520; c.height = 120;
  const ctx = c.getContext("2d");
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.fillStyle = "#050303";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Auto-fit
  let size = 64;
  do {
    ctx.font = `${size}px ${fontFamily}`;
    if (ctx.measureText(name).width <= c.width - 40) break;
    size -= 2;
  } while (size > 20);
  ctx.fillText(name, c.width / 2, c.height / 2);
  return c.toDataURL("image/png");
}

function SignaturePad({ label, value, adoptedAt, signerName, onNameChange, onChange, onAdopt, testId }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(!value);
  const [mode, setMode] = useState("type"); // "type" | "draw"
  const [selectedStyle, setSelectedStyle] = useState(SIG_FONTS[0].key);
  const adopted = !!adoptedAt;

  useEffect(() => {
    if (mode !== "draw") return;
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#050303";
    if (value && value.startsWith("data:image")) {
      const img = new Image();
      img.onload = () => { ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.drawImage(img, 0, 0); };
      img.src = value;
    }
  }, [value, mode]);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const p = e.touches?.[0] ?? e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  };
  const start = (e) => {
    if (adopted) return;
    e.preventDefault(); drawing.current = true;
    const { x, y } = pos(e);
    const ctx = ref.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(x, y);
    setEmpty(false);
  };
  const move = (e) => {
    if (adopted || !drawing.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = ref.current.getContext("2d");
    ctx.lineTo(x, y); ctx.stroke();
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(ref.current.toDataURL("image/png"));
  };
  const clearDraw = () => {
    if (adopted) return;
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange(""); setEmpty(true);
  };

  const activeFont = SIG_FONTS.find((f) => f.key === selectedStyle) || SIG_FONTS[0];

  // In "type" mode, keep a preview PNG in sync with the typed name + style.
  // Intentionally omit `onChange` from deps: parent creates it inline each render,
  // which would cause an infinite update loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (mode !== "type" || adopted) return;
    if (!signerName?.trim()) { onChange(""); return; }
    onChange(renderNameToPng(signerName.trim(), activeFont.family));
  }, [signerName, selectedStyle, mode, adopted, activeFont.family]);

  const stampLabel = adoptedAt
    ? new Date(adoptedAt).toLocaleString("en-US", {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        timeZoneName: "short",
      })
    : "";

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <Label className="text-xs uppercase tracking-wider text-[#050303]/60">{label}</Label>
        {!adopted && (
          <div className="flex items-center gap-1 text-xs">
            <button
              type="button"
              onClick={() => setMode("type")}
              className={`px-3 py-1 rounded-full ${mode === "type" ? "bg-[#050303] text-white" : "bg-[#e1b7c1]/40 text-[#050303]/70 hover:bg-[#e1b7c1]/70"}`}
              data-testid={`${testId}-mode-type`}
            >
              <Type size={11} className="inline mr-1" /> Type & choose style
            </button>
            <button
              type="button"
              onClick={() => { setMode("draw"); onChange(""); }}
              className={`px-3 py-1 rounded-full ${mode === "draw" ? "bg-[#050303] text-white" : "bg-[#e1b7c1]/40 text-[#050303]/70 hover:bg-[#e1b7c1]/70"}`}
              data-testid={`${testId}-mode-draw`}
            >
              <PenTool size={11} className="inline mr-1" /> Draw
            </button>
          </div>
        )}
      </div>

      {mode === "type" && !adopted && (
        <div className="space-y-3">
          {!signerName?.trim() && (
            <input
              type="text"
              placeholder="Type your full legal name"
              value={signerName || ""}
              onChange={(e) => onNameChange?.(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-[#d1cece] rounded-md focus:outline-none focus:border-[#bf858e]"
              data-testid={`${testId}-name`}
            />
          )}
          {signerName?.trim() && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SIG_FONTS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setSelectedStyle(f.key)}
                  className={`text-left rounded-md border-2 px-4 py-3 transition-all ${selectedStyle === f.key ? "border-[#050303] bg-[#fcf5f7]" : "border-[#d1cece] hover:border-[#bf858e]"}`}
                  data-testid={`${testId}-style-${f.key}`}
                >
                  <div className="text-[10px] uppercase tracking-wider text-[#050303]/50 mb-1">{f.label}</div>
                  <div
                    style={{ fontFamily: f.family, fontSize: "28px", color: "#050303", lineHeight: 1 }}
                    className="truncate"
                  >
                    {signerName.trim()}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === "draw" && (
        <div className="relative">
          <canvas
            ref={ref}
            width={520}
            height={120}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            className={`w-full h-[120px] border-2 border-dashed rounded-md bg-white touch-none ${adopted ? "border-[#050303] cursor-not-allowed" : "border-[#d1cece] cursor-crosshair"}`}
            data-testid={testId}
          />
          {empty && !adopted && (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-[#050303]/40 pointer-events-none">
              Sign here with your mouse or finger…
            </div>
          )}
          {!adopted && !empty && (
            <button type="button" onClick={clearDraw} className="absolute top-2 right-2 text-xs text-[#bf858e] hover:text-[#050303] flex items-center gap-1" data-testid={`${testId}-clear`}>
              <RotateCcw size={11} /> Clear
            </button>
          )}
        </div>
      )}

      {adopted && (
        <div className="relative border-2 border-[#050303] rounded-md bg-white p-2">
          {value && <img src={value} alt="Adopted signature" className="max-h-[100px] object-contain" />}
          <div className="absolute top-1 right-2 flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#bf858e] bg-white/90 rounded px-1.5 py-0.5">
            <ShieldCheck size={11} /> Adopted
          </div>
        </div>
      )}

      <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
        {adopted ? (
          <div className="text-xs text-[#050303]/70">
            <span className="text-[#050303]/50">Digitally adopted:</span>{" "}
            <span className="font-mono">{stampLabel}</span>
          </div>
        ) : (
          <div className="text-xs text-[#050303]/50">
            {mode === "type"
              ? "Type your name, pick a style, then click Adopt to lock and timestamp."
              : "Sign above, then click Adopt to lock and timestamp."}
          </div>
        )}
        {!adopted && (
          <button
            type="button"
            disabled={!value}
            onClick={onAdopt}
            className="bg-[#050303] text-white pill-btn text-xs hover:bg-[#bf858e] disabled:opacity-40 flex items-center gap-1 shrink-0"
            data-testid={`${testId}-adopt`}
          >
            <ShieldCheck size={12} /> Adopt Signature
          </button>
        )}
      </div>
    </div>
  );
}

const Field = ({ value, onChange, label, placeholder, type = "text", prefix, testId }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-[#050303]/60">{label}</Label>
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#050303]/50">{prefix}</span>}
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        className={prefix ? "pl-7" : ""}
        data-testid={testId}
      />
    </div>
  </div>
);

/* ---------- Page ---------- */
const emptySig = (role = "") => ({
  name: "",
  role,
  date: new Date().toISOString().slice(0, 10),
  signature_png: "",
  adopted_at: "",
});

export default function PayAtClosing() {
  const { user } = useAuth();
  const [f, setF] = useState({
    closing_date: "", attorney_firm: "", property_address: "",
    attorney_email: "", attorney_phone: "", attorney_fax: "",
    fmls_number: "", gamls_number: "",
    buyer: "", seller: "",
    core_agent_name: user?.name || "", broker_code: "CORA01", represents: "Buyer",
    contract_sales_price: "", commission_pct: "3.0", flat_fee_amount: "",
    additional_listing_fees: "", broker_bonus: "",
    referral_fee_pct: "", referral_pay_to: "", referral_fee_amount: "",
    earnest_money_held: "",
    fmls_fee: "", transaction_fee: "", additional_fees: "",
  });
  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e?.target?.value ?? e }));
  const [signatures, setSignatures] = useState([emptySig("Agent"), emptySig("Broker or CEO")]);
  const [busy, setBusy] = useState(false);

  const num = (k) => Number(f[k] || 0);

  // FMLS fee is a flat 0.12% of the contract sales price. Auto-fill it the
  // moment the sales price changes — but only key off `contract_sales_price`
  // so an agent who manually adjusts the FMLS field afterward keeps their
  // override until the next sales-price edit.
  useEffect(() => {
    const price = Number(f.contract_sales_price || 0);
    const calc = price > 0 ? +(price * 0.0012).toFixed(2) : "";
    setF((prev) => (String(prev.fmls_fee) === String(calc) ? prev : { ...prev, fmls_fee: calc }));
  }, [f.contract_sales_price]);

  const gross = useMemo(() => {
    const base = num("commission_pct") ? num("contract_sales_price") * (num("commission_pct") / 100) : num("flat_fee_amount");
    return base + num("additional_listing_fees") + num("broker_bonus");
  }, [f.contract_sales_price, f.commission_pct, f.flat_fee_amount, f.additional_listing_fees, f.broker_bonus]);
  const subtotal = useMemo(
    () => Math.max(0, gross - num("referral_fee_amount") - num("earnest_money_held")),
    [gross, f.referral_fee_amount, f.earnest_money_held]
  );
  const total_fees = num("fmls_fee") + num("transaction_fee") + num("additional_fees");
  const agent_share = Math.max(0, subtotal - total_fees);

  const submit = async () => {
    if (!f.property_address) return toast.error("Property address required");
    const agentSig = signatures[0];
    if (!agentSig?.signature_png) return toast.error("Agent signature required");
    if (!agentSig?.adopted_at) return toast.error("Please click 'Adopt Signature' to authenticate & timestamp");
    setBusy(true);
    try {
      const body = {
        ...f,
        contract_sales_price: num("contract_sales_price"),
        commission_pct: num("commission_pct"),
        flat_fee_amount: num("flat_fee_amount"),
        additional_listing_fees: num("additional_listing_fees"),
        broker_bonus: num("broker_bonus"),
        referral_fee_pct: num("referral_fee_pct"),
        referral_fee_amount: num("referral_fee_amount"),
        earnest_money_held: num("earnest_money_held"),
        fmls_fee: num("fmls_fee"),
        transaction_fee: num("transaction_fee"),
        additional_fees: num("additional_fees"),
        agent_disbursement: agent_share,
        brokerage_disbursement: total_fees,
        signatures: signatures.filter(s => s.signature_png && s.adopted_at),
      };
      const token = localStorage.getItem("core_token");
      const resp = await fetch(`${API}/pay-at-closing`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const emailSent = resp.headers.get("x-email-sent") === "true";
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CORE-PayAtClosing-${(f.property_address || "form").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(emailSent
        ? "PDF downloaded — sent to Latoya for approval & broker signature"
        : "PDF downloaded — broker email did not send (check logs)");
    } catch {
      toast.error("Submission failed");
    } finally { setBusy(false); }
  };

  const fld = (k, label, opts = {}) => (
    <Field
      value={f[k]}
      onChange={set(k)}
      label={label}
      testId={`pac-${k}`}
      {...opts}
    />
  );

  return (
    <div className="space-y-6 max-w-5xl" data-testid="pay-at-closing-page">
      <div className="flex items-center gap-3">
        <Link to="/tools" className="text-[#050303]/60 hover:text-[#050303]" data-testid="pac-back">
          <ArrowLeft size={18} />
        </Link>
        <div>
          <div className="overline">Brokerage · Form #11</div>
          <h1 className="font-heading text-4xl mt-1">Pay-At-Closing Instructions</h1>
          <p className="text-sm text-[#050303]/60 mt-2">
            Fill, sign, and download. A signed copy is auto-emailed to Latoya Winfrey.
          </p>
        </div>
      </div>

      <div className="core-card p-6 bg-[#e1b7c1]/30 border-[#e1b7c1]">
        <div className="text-center font-heading text-lg tracking-wide">
          ★★★ PAY AT CLOSE IS APPROVED FOR THIS CORE AGENT ★★★
        </div>
      </div>

      <section className="core-card p-6 space-y-4">
        <h2 className="font-heading text-xl">Property & Attorney</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fld("closing_date", "Closing Date", {type: "date"})}
          {fld("attorney_firm", "Title Attorney Firm / Contact")}
          <div className="md:col-span-2">{fld("property_address", "Property Address")}</div>
          {fld("attorney_email", "Email", {type: "email"})}
          {fld("attorney_phone", "Phone")}
          {fld("attorney_fax", "Fax")}
          {fld("fmls_number", "FMLS #")}
          {fld("gamls_number", "GAMLS #")}
          {fld("buyer", "Buyer")}
          {fld("seller", "Seller")}
        </div>
      </section>

      <section className="core-card p-6 space-y-4">
        <h2 className="font-heading text-xl">CORE Associate</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {fld("core_agent_name", "Agent Name")}
          {fld("broker_code", "Broker Code")}
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#050303]/60">Represents</Label>
            <div className="flex gap-4 mt-2">
              {["Buyer", "Seller"].map(r => (
                <label key={r} className="flex items-center gap-2 cursor-pointer" data-testid={`pac-represents-${r.toLowerCase()}`}>
                  <input type="radio" name="represents" checked={f.represents === r} onChange={() => setF({ ...f, represents: r })} className="accent-[#050303]" />
                  <span className="text-sm">{r}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="core-card p-6 space-y-4">
        <h2 className="font-heading text-xl">Commission Calculation</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fld("contract_sales_price", "Contract Sales Price", {type: "number", prefix: "$"})}
          {fld("commission_pct", "% Commission Earned", {type: "number", placeholder: "e.g. 3.0"})}
          {fld("flat_fee_amount", "…or Flat Fee Amount", {type: "number", prefix: "$"})}
          {fld("additional_listing_fees", "Additional Listing Fees", {type: "number", prefix: "$"})}
          {fld("broker_bonus", "Broker / Selling Commission Bonus", {type: "number", prefix: "$"})}
        </div>
        <div className="bg-[#fcf5f7] rounded-md p-4 flex items-center justify-between" data-testid="pac-gross">
          <span className="font-semibold">Gross Commission Due to CORE R&amp;A LLC</span>
          <span className="font-heading text-xl text-[#050303]">{money(gross)}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {fld("referral_fee_pct", "Referral Fee %", {type: "number"})}
          {fld("referral_pay_to", "Pay Referral To")}
          {fld("referral_fee_amount", "Referral Fee Amount", {type: "number", prefix: "$"})}
          <div className="md:col-span-3">{fld("earnest_money_held", "Less Earnest Money Held by CORE R&A LLC", {type: "number", prefix: "$"})}</div>
        </div>
        <div className="bg-[#fcf5f7] rounded-md p-4 flex items-center justify-between" data-testid="pac-subtotal">
          <span className="font-semibold">Commission Subtotal for Disbursement</span>
          <span className="font-heading text-xl text-[#050303]">{money(subtotal)}</span>
        </div>
      </section>

      <section className="core-card p-6 space-y-4">
        <h2 className="font-heading text-xl">Additional Fees</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {fld("fmls_fee", "FMLS Fee (0.12% of sale)", {type: "number", prefix: "$"})}
          {fld("transaction_fee", "Transaction Fee ($300 / $500 / $800)", {type: "number", prefix: "$"})}
          {fld("additional_fees", "Additional Fees", {type: "number", prefix: "$"})}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-[#050303] text-white rounded-md p-4 flex items-center justify-between" data-testid="pac-agent-total">
            <span className="font-semibold">Total Due to CORE Agent</span>
            <span className="font-heading text-2xl">{money(agent_share)}</span>
          </div>
          <div className="bg-[#050303] text-white rounded-md p-4 flex items-center justify-between" data-testid="pac-brokerage-total">
            <span className="font-semibold">Total Due to CORE R&amp;A LLC</span>
            <span className="font-heading text-2xl">{money(total_fees)}</span>
          </div>
        </div>
      </section>

      <section className="core-card p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl flex items-center gap-2"><PenTool size={18} /> Signatures</h2>
          <button
            type="button"
            onClick={() => setSignatures(s => [...s, emptySig("Co-signer")])}
            className="text-sm text-[#bf858e] hover:text-[#050303] flex items-center gap-1"
            data-testid="pac-add-signer"
          >
            <Plus size={14} /> Add another signer
          </button>
        </div>
        {signatures.map((sig, i) => (
          <div key={i} className="border border-[#d1cece] rounded-lg p-4 space-y-3" data-testid={`pac-sig-block-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-[#050303]/60">Signer {i + 1}</span>
              {i > 0 && (
                <button
                  type="button"
                  onClick={() => setSignatures(s => s.filter((_, j) => j !== i))}
                  className="text-[#050303]/40 hover:text-[#bf858e]"
                  data-testid={`pac-remove-signer-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#050303]/60">Name</Label>
                <Input value={sig.name} onChange={(e) => setSignatures(s => s.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} data-testid={`pac-sig-name-${i}`} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#050303]/60">Role</Label>
                <Input value={sig.role} onChange={(e) => setSignatures(s => s.map((x, j) => j === i ? { ...x, role: e.target.value } : x))} data-testid={`pac-sig-role-${i}`} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#050303]/60">Date</Label>
                <Input type="date" value={sig.date} onChange={(e) => setSignatures(s => s.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} data-testid={`pac-sig-date-${i}`} />
              </div>
            </div>
            <SignaturePad
              label="Signature"
              value={sig.signature_png}
              adoptedAt={sig.adopted_at}
              signerName={sig.name}
              onNameChange={(v) => setSignatures(s => s.map((x, j) => j === i ? { ...x, name: v } : x))}
              onChange={(v) => setSignatures(s => s.map((x, j) => j === i ? { ...x, signature_png: v, adopted_at: "" } : x))}
              onAdopt={() => {
                if (!sig.signature_png) return;
                if (!sig.name.trim()) {
                  toast.error("Enter signer name before adopting");
                  return;
                }
                setSignatures(s => s.map((x, j) => j === i ? { ...x, adopted_at: new Date().toISOString() } : x));
                toast.success("Signature adopted and timestamped");
              }}
              testId={`pac-sig-pad-${i}`}
            />
          </div>
        ))}
      </section>

      <div className="flex items-center justify-end gap-3 pb-10">
        <button
          onClick={submit}
          disabled={busy}
          className="bg-[#050303] text-white pill-btn hover:bg-[#bf858e] disabled:opacity-50 flex items-center gap-2"
          data-testid="pac-submit"
        >
          <Download size={16} />
          {busy ? "Generating…" : "Sign, download & send to Broker for approval"}
        </button>
      </div>
    </div>
  );
}
